import { createContext, useContext, useState, useEffect } from 'react';
import type { ReactNode } from 'react';
import { useAccount, useSignMessage, useDisconnect } from 'wagmi';
import type { WalletType } from '@types';
import {
  authService,
  SIGN_MESSAGE,
  clearRefId,
} from '@/shared/services/auth.service';
import { useAuthStore } from '@app/stores';

interface User {
  id: string;
  address: string;
  username?: string;
  level: number;
  experience: number;
  coins: number;
  energy?: number;
  walletType?: WalletType;
  isActive?: boolean;
  sei?: number;
  gold?: number;
  scores?: number;
  pvpRankScore?: number;
  totalCheckIn: number;
}

interface WalletContextValue {
  user: User | null;
  accessToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (walletType: WalletType, walletAddress?: string) => Promise<void>;
  logout: () => void;
}

const WalletContext = createContext<WalletContextValue | undefined>(undefined);

export function WalletProvider({ children }: { children: ReactNode }) {
  const [isLoading, setIsLoading] = useState(true);
  const {
    user,
    accessToken,
    refreshTokenValue,
    isAuthenticated,
    setAuth,
    clearAuth,
  } = useAuthStore();

  const { address } = useAccount();
  const { signMessageAsync } = useSignMessage();
  const { disconnect } = useDisconnect();

  // Auto verify token on mount by fetching user info
  useEffect(() => {
    const verifyStoredToken = async () => {
      if (accessToken) {
        try {
          const userProfile = await authService.getUserProfile();

          // Map API response to User interface
          const userData: User = {
            id: userProfile.id,
            address: userProfile.walletAddress,
            username: userProfile.id,
            level: 1,
            experience: 0,
            coins: userProfile.userAssets.gold || 0,
            energy: userProfile.energy,
            walletType: userProfile.walletType as WalletType,
            isActive: userProfile.isActive,
            sei: userProfile.userAssets.sei || 0,
            gold: userProfile.userAssets.gold,
            scores: userProfile.userAssets.scores,
            pvpRankScore: userProfile.userAssets.pvpRankScore || 0,
            totalCheckIn: userProfile.totalCheckIn || 0,
          };

          setAuth(userData, accessToken, refreshTokenValue || '');
        } catch {
          clearAuth();
        }
      }

      setIsLoading(false);
    };

    verifyStoredToken();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async (walletType: WalletType, walletAddress?: string) => {
    const addressToUse = walletAddress || address;

    if (!addressToUse) {
      throw new Error('Wallet not connected');
    }

    setIsLoading(true);

    try {
      // Request signature from wallet
      const signature = await signMessageAsync({ message: SIGN_MESSAGE });

      // Call login API
      const loginResponse = await authService.login(
        walletType,
        addressToUse,
        signature
      );

      // Save tokens first so axios interceptor can use them
      const tempUser: User = {
        id: '',
        address: addressToUse,
        level: 1,
        experience: 0,
        coins: 0,
        walletType,
        isActive: true,
        totalCheckIn: 0,
      };
      setAuth(tempUser, loginResponse.accessToken, loginResponse.refreshToken);

      // Fetch full user info
      const userProfile = await authService.getUserProfile();

      // Map API response to User interface
      const userData: User = {
        id: userProfile.id,
        address: userProfile.walletAddress,
        username: userProfile.id,
        level: 1,
        experience: 0,
        coins: userProfile.userAssets.gold || 0,
        energy: userProfile.energy,
        walletType: userProfile.walletType as WalletType,
        isActive: userProfile.isActive,
        sei: userProfile.userAssets.sei || 0,
        gold: userProfile.userAssets.gold,
        scores: userProfile.userAssets.scores,
        pvpRankScore: userProfile.userAssets.pvpRankScore || 0,
        totalCheckIn: userProfile.totalCheckIn || 0,
      };

      // Save tokens and user to zustand store (it will handle persistence)
      setAuth(userData, loginResponse.accessToken, loginResponse.refreshToken);

      // Clear referral ID after successful login
      clearRefId();
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    clearAuth();
    disconnect();
  };

  const value: WalletContextValue = {
    user,
    accessToken,
    isAuthenticated,
    isLoading,
    login,
    logout,
  };

  return (
    <WalletContext.Provider value={value}>{children}</WalletContext.Provider>
  );
}

export function useWallet() {
  const context = useContext(WalletContext);

  if (context === undefined) {
    throw new Error('useWallet must be used within WalletProvider');
  }

  return context;
}
